package er.prateek.crossly.notifications

/**
 * The per-platform local (on-device) notification scheduler, reduced to what this app needs:
 * one daily reminder, rescheduled every app open. Mirrors [er.prateek.crossly.ads.AdHost]'s
 * shape -- an `isSupported` flag plus a [NoOpLocalNotificationScheduler] fallback so callers
 * never need to branch on platform.
 *
 * Android and iOS resolve "daily at this time" very differently -- see the `androidMain`/
 * `iosMain` actuals for why -- but both converge on this same small surface.
 */
interface LocalNotificationScheduler {
    val isSupported: Boolean

    /** Creates the Android notification channel. No-op everywhere else. Safe to call repeatedly. */
    fun ensureChannel()

    /**
     * (Re)schedules the single daily reminder for the next occurrence of [hour]:[minute] local
     * time, replacing any pending one. [content] is used as-is on platforms that bake content in
     * at schedule time (iOS); Android re-derives fresher content at delivery time instead -- see
     * the androidMain actual.
     */
    fun scheduleDailyReminder(hour: Int, minute: Int, content: ReminderContent)

    fun cancelDailyReminder()
}

/** Desktop, web, and the pre-registration state on every other platform. */
object NoOpLocalNotificationScheduler : LocalNotificationScheduler {
    override val isSupported: Boolean = false
    override fun ensureChannel() = Unit
    override fun scheduleDailyReminder(hour: Int, minute: Int, content: ReminderContent) = Unit
    override fun cancelDailyReminder() = Unit
}

/** Per-platform binding, mirroring [er.prateek.crossly.ads.AdHostProvider]. */
expect object NotificationSchedulerProvider {
    val scheduler: LocalNotificationScheduler
}
